// "What have you got for these jobs?" — Orion writes in asking whether a job
// ran, quoting one row of their own dispatch sheet. This builds the answer in
// the same shape: their columns, their colours, plus the Status column their
// sheet doesn't carry, which is the thing they're actually asking about.
//
// Copies as rich HTML so pasting into Gmail keeps the table rather than
// collapsing it to text; a plain-text version rides along for clients that
// won't take HTML.
//
// Loads jobs itself instead of reading the inbox's list: the question usually
// spans a completed job and a live one, which sit on different tabs there.

import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

// "2026-10-09" → "Friday, October 9, 2026" — the long form Orion writes.
function longDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `${DAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// "15:30" → "1530hrs". Blank stays blank rather than printing "0000hrs".
function hrs(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || "").trim());
  if (!m) return "";
  return `${String(m[1]).padStart(2, "0")}${m[2]}hrs`;
}

// "Elemetal - Charlotte, NC" from the first real stop. Orion names the
// location and its city, not the street.
function cityState(address) {
  const parts = String(address || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) return "";
  // street, city, "NC 28208", country → city is [1]; city, "NC 28208" → [0]
  const city = parts.length >= 3 ? parts[1] : parts[0];
  const stateZip = parts.length >= 3 ? parts[2] : parts[1];
  const state = String(stateZip || "").trim().split(/\s+/)[0] || "";
  return city ? `${city}${state ? `, ${state}` : ""}` : "";
}

function routeLabel(job) {
  const real = (job.stops || []).filter((s) => s.type !== "airport");
  if (!real.length) return "—";
  const label = (s) => {
    const where = cityState(s.address);
    const name = s.locationName || "—";
    return where ? `${name} - ${where}` : name;
  };
  // A→B reads as a route; a single stop reads on its own.
  if (real.length === 1) return label(real[0]);
  return `${label(real[0])} > ${label(real[real.length - 1])}`;
}

// Surnames only — Orion's sheet prints "Thompson", not "Scott Thompson".
function guardNames(job) {
  const names = (job.guardAssignments || [])
    .slice()
    .sort((a, b) => (a.guardNumber || 0) - (b.guardNumber || 0))
    .map((g) => String(g.guardName || "").trim().split(/\s+/).pop())
    .filter(Boolean);
  return [...new Set(names)].join(", ");
}

const TYPE_LABEL = { inbound: "Inbound", outbound: "Outbound", direct: "Direct", roundtrip: "Round trip" };

// The column their sheet doesn't have — the actual answer to "did this run?"
function statusCell(job) {
  if (job.status === "cancelled") {
    return job.tripCancelledReason ? `Cancelled — ${job.tripCancelledReason}` : "Cancelled";
  }
  const real = (job.stops || []).filter((s) => s.type !== "airport");
  const done = real.filter((s) => s.completedAt || s.noShow).length;
  const pods = (job.stops || []).some((s) => s.podPhotoUrl)
    || (job.packages || []).some((p) => p.podPhotoUrl);
  if (job.status === "complete") {
    return `Completed${real.length ? ` · ${done}/${real.length} stops` : ""}${pods ? " · POD on file" : ""}`;
  }
  if (job.status === "inProgress") return `In progress · ${done}/${real.length} stops done`;
  if (job.status === "pool") return "Not assigned yet";
  if (job.status === "intake") return "Not scheduled yet";
  return "Assigned, not started";
}

function rowFor(job) {
  const firstTimed = (job.stops || []).find((s) => s.scheduledTime);
  return {
    jobNumber: job.jobNumber || "—",
    date: longDate(job.scheduledDate),
    route: routeLabel(job),
    time: hrs(job.scheduledTime || firstTimed?.scheduledTime || ""),
    gateway: job.airportCode || "",
    type: TYPE_LABEL[job.jobType] || job.jobType || "",
    guards: job.numGuards || 1,
    names: guardNames(job),
    status: statusCell(job),
  };
}

const esc = (v) => String(v == null ? "" : v)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Their palette: cyan job number, magenta guard name, black hairline borders.
function tableHtml(rows) {
  const td = (v, bg) =>
    `<td style="border:1px solid #000;padding:4px 8px;font-family:Calibri,Arial,sans-serif;font-size:11pt;white-space:nowrap;${bg ? `background:${bg};` : ""}">${esc(v)}</td>`;
  const body = rows.map((r) => "<tr>"
    + td(r.jobNumber, "#00B0F0")
    + td(r.date)
    + td(r.route)
    + td(r.time)
    + td(r.gateway)
    + td(r.type)
    + td(r.guards)
    + td(r.names, "#FF00FF")
    + td(r.status)
    + "</tr>").join("");
  return `<table style="border-collapse:collapse;border:1px solid #000"><tbody>${body}</tbody></table>`;
}

function tableText(rows) {
  return rows.map((r) => [
    r.jobNumber, r.date, r.route, r.time, r.gateway, r.type, r.guards, r.names, r.status,
  ].join(" | ")).join("\n");
}

export default function OrionReplyModal({ companyId, onClose }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]); // job ids, in the order ticked
  const [copied, setCopied] = useState("");

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "jobs"), where("companyId", "==", companyId)));
        if (!dead) setJobs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.warn("Couldn't load jobs for the Orion reply", err);
      }
      if (!dead) setLoading(false);
    })();
    return () => { dead = true; };
  }, [companyId]);

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return jobs
      .filter((j) => `${j.jobNumber || ""} ${j.brokerName || ""} ${routeLabel(j)}`.toLowerCase().includes(needle))
      .sort((a, b) => String(b.scheduledDate || "").localeCompare(String(a.scheduledDate || "")))
      .slice(0, 25);
  }, [jobs, q]);

  // Ticked jobs stay listed even when the search moves on — that's the whole
  // point of building a reply about two jobs with different numbers.
  const pickedJobs = picked.map((id) => jobs.find((j) => j.id === id)).filter(Boolean);
  const rows = pickedJobs.map(rowFor);

  function toggle(id) {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setCopied("");
  }

  async function copyTable() {
    const html = tableHtml(rows);
    const text = tableText(rows);
    try {
      await navigator.clipboard.write([new window.ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      })]);
      setCopied("Copied — paste straight into the reply.");
    } catch {
      // Firefox and older Safari have no ClipboardItem; fall back to plain text
      // so the owner still gets something pasteable.
      try {
        await navigator.clipboard.writeText(text);
        setCopied("Copied as plain text — this browser can't carry the table.");
      } catch {
        setCopied("Couldn't reach the clipboard. Select the preview below and copy it.");
      }
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} style={{
        width: "min(900px, 100%)", maxHeight: "90vh", display: "flex", flexDirection: "column", background: "#fff",
      }}>
        <div className="panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="panel-title">Reply to Orion</div>
            <div className="panel-subtitle">
              Pick the jobs they asked about. Copies a table in their format, with what you have on each.
            </div>
          </div>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: 14, overflowY: "auto", flex: 1 }}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a job number — e.g. 4859"
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #ddd", borderRadius: 7, fontSize: 13, boxSizing: "border-box" }}
          />

          {loading ? (
            <div style={{ fontSize: 12, color: "#888", padding: "10px 2px" }}>Loading jobs…</div>
          ) : q.trim() && matches.length === 0 ? (
            <div style={{ fontSize: 12, color: "#a32d2d", padding: "10px 2px" }}>
              No job matches “{q.trim()}”. If Orion is asking about a number you never entered, that's the answer.
            </div>
          ) : (
            <div style={{ marginTop: 8 }}>
              {matches.map((j) => (
                <label key={j.id} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "7px 8px",
                  borderBottom: "1px solid #f3f3f3", fontSize: 12, cursor: "pointer",
                }}>
                  <input type="checkbox" checked={picked.includes(j.id)} onChange={() => toggle(j.id)}
                    style={{ width: 16, height: 16 }} />
                  <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#185FA5", width: 120 }}>
                    {j.jobNumber || "—"}
                  </span>
                  <span style={{ width: 92, color: "#666" }}>{j.scheduledDate || "—"}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {routeLabel(j)}
                  </span>
                  <span style={{ color: "#888", whiteSpace: "nowrap" }}>{statusCell(j)}</span>
                </label>
              ))}
            </div>
          )}

          {rows.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", marginBottom: 6 }}>
                {rows.length} job{rows.length === 1 ? "" : "s"} selected · preview
              </div>
              <div style={{ overflowX: "auto", border: "1px solid #eee", borderRadius: 6, padding: 8 }}
                dangerouslySetInnerHTML={{ __html: tableHtml(rows) }} />
              <div style={{ fontSize: 10, color: "#888", marginTop: 6 }}>
                Columns: job #, date, route, time, gateway, type, guards, guard, status.
                The last one is yours — their sheet doesn't have it.
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "12px 14px", borderTop: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: copied.startsWith("Couldn't") ? "#a32d2d" : "#2d7a2d" }}>{copied}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={onClose}>Close</button>
            <button className="btn btn-blue" disabled={rows.length === 0} onClick={copyTable}>
              📋 Copy table
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
